import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';

const GTM_CONTAINER_ID = 'GTM-NQV4RHHK';

const PUBLIC_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/candidates$/,
  /^\/company\/about$/,
  /^\/company\/contact$/,
  /^\/company\/pricing$/,
  /^\/jobs$/,
  /^\/jobs\/[^/?#]+$/,
  /^\/legal\/.*/,
  /^\/auth\/.*/,
  /^\/candidate-auth$/,
  /^\/company-auth$/,
  /^\/schedule\/.*/,
];

function isPublicRoute(location: string): boolean {
  const pathname = location.split(/[?#]/)[0] ?? '';
  return PUBLIC_ROUTE_PATTERNS.some(pattern => pattern.test(pathname));
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

function initializeDataLayer() {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  
  function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  }
  
  if (typeof window.gtag !== 'function') {
    window.gtag = gtag;
  }
  
  window.gtag('consent', 'default', {
    'analytics_storage': 'denied',
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
  });
}

if (typeof window !== 'undefined') {
  initializeDataLayer();
}

export function useGoogleAnalytics() {
  const [location] = useLocation();
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    const isPublic = isPublicRoute(location);

    if (!isPublic) {
      return;
    }

    if (!scriptLoadedRef.current) {
      scriptLoadedRef.current = true;

      const loadGTM = () => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'gtm.start': new Date().getTime(),
          event: 'gtm.js'
        });

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
        document.head.appendChild(script);
      };

      // Defer GTM until after page is interactive to reduce TBT on initial load
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(loadGTM, { timeout: 4000 });
      } else {
        setTimeout(loadGTM, 3500);
      }
    }
  }, [location]);
}
